import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  numberAttribute,
} from '@angular/core';

export type DsGridGap = 'none' | 'xs' | 's' | 'm' | 'l' | 'xl';
export type DsGridAlign = 'start' | 'center' | 'end' | 'stretch';
export type DsGridJustify = 'start' | 'center' | 'end' | 'stretch';
export type DsGridContainer = 'none' | 's' | 'm' | 'l' | 'xl';

const GAP_TOKENS: Record<DsGridGap, string> = {
  none: '0px',
  xs: 'var(--ds-grid-gap-xs)',
  s: 'var(--ds-grid-gap-s)',
  m: 'var(--ds-grid-gap-m)',
  l: 'var(--ds-grid-gap-l)',
  xl: 'var(--ds-grid-gap-xl)',
};

const CONTAINER_TOKENS: Record<Exclude<DsGridContainer, 'none'>, string> = {
  s: 'var(--ds-grid-container-s)',
  m: 'var(--ds-grid-container-m)',
  l: 'var(--ds-grid-container-l)',
  xl: 'var(--ds-grid-container-xl)',
};

@Component({
  selector: 'ds-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<ng-content />`,
  styles: [`
    :host {
      --ds-grid-columns-current: var(--ds-grid-columns, 12);
      --ds-grid-gap-current: var(--ds-grid-gap-m);
      display: grid;
      grid-template-columns: repeat(var(--ds-grid-columns-current), minmax(0, 1fr));
      gap: var(--ds-grid-gap-current);
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
    }

    :host(.ds-grid--container) {
      max-width: var(--ds-grid-container-current);
      margin-inline: auto;
    }
  `],
})
export class DsGridComponent {
  @HostBinding('style.--ds-grid-columns-current')
  private columnsStyle = '12';

  @HostBinding('style.--ds-grid-gap-current')
  private gapStyle = GAP_TOKENS.m;

  @HostBinding('style.align-items')
  alignItems: DsGridAlign = 'stretch';

  @HostBinding('style.justify-items')
  justifyItems: DsGridJustify = 'stretch';

  @HostBinding('style.--ds-grid-container-current')
  private containerStyle: string | null = null;

  @HostBinding('class.ds-grid--container')
  private isContainer = false;

  @Input({ transform: numberAttribute })
  set columns(value: number) {
    this.columnsStyle = String(this.clamp(value || 12, 1, 24));
  }

  @Input()
  set gap(value: DsGridGap) {
    this.gapStyle = GAP_TOKENS[value] ?? GAP_TOKENS.m;
  }

  @Input()
  set align(value: DsGridAlign) {
    this.alignItems = value ?? 'stretch';
  }

  @Input()
  set justify(value: DsGridJustify) {
    this.justifyItems = value ?? 'stretch';
  }

  @Input()
  set container(value: DsGridContainer) {
    if (!value || value === 'none') {
      this.isContainer = false;
      this.containerStyle = null;
      return;
    }

    this.isContainer = true;
    this.containerStyle = CONTAINER_TOKENS[value];
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(Math.trunc(value), min), max);
  }
}
